import { Formik } from 'formik';
import { useState, useEffect, useMemo } from 'react';
import { Button, FormGroup, Label, ModalBody } from 'reactstrap';
import * as yup from 'yup';
import { YIELD_UNITS_BY_DEFAULT } from '../../constants/units';
import { useUnits } from '../../contexts/UnitContext';
import DropdownButton from '../basics/DropdownButton';
import Modal from '../basics/Modal';
import {
  SubmitButton,
  TemplateForm,
  ValidatedInput,
} from './template-components';

const customUnitTypeOptions = {
  lbsPerUnit: {
    getDisplayName: (yieldDisplayUnit) => `${yieldDisplayUnit} / unit`,
    value: 'lbsPerUnit',
  },
  unitsPerTree: { getDisplayName: () => 'units / tree', value: 'unitsPerTree' },
};

const unitsSchema = yup.object().shape({
  unit: yup.string().required('Required'),
  lbsPerUnit: yup
    .number()
    .positive('Must be positive')
    .when('unitsPerTree', {
      is: (unitsPerTree) => unitsPerTree == null,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
});

const RevenueUnitsModal = ({
  editing,
  yieldUnit,
  onClose,
  updateStream,
  ...props
}) => {
  const unitSpec = YIELD_UNITS_BY_DEFAULT[yieldUnit];
  const { displayUnit: yieldDisplayUnit } = useUnits(unitSpec);

  // customUnitType is a derived value based on which unit field
  // (unitsPerTree or lbsPerUnit) is defined on the revenue stream.
  const [customUnitType, setCustomUnitType] = useState(
    customUnitTypeOptions.lbsPerUnit,
  );

  useEffect(() => {
    if (!editing) return;
    setCustomUnitType(
      editing.unitsPerTree
        ? customUnitTypeOptions.unitsPerTree
        : customUnitTypeOptions.lbsPerUnit,
    );
  }, [editing]);

  const onSubmit = (values) => {
    updateStream(values);
    onClose();
  };

  const unitTypeOptions = useMemo(
    () =>
      Object.values(customUnitTypeOptions).map((option) => ({
        ...option,
        name: option.getDisplayName(yieldDisplayUnit),
      })),
    [yieldDisplayUnit],
  );

  const selectedUnitName = customUnitType.getDisplayName(yieldDisplayUnit);

  if (!editing) return null;

  return (
    <Modal isOpen={!!editing} onClose={onClose} {...props}>
      <ModalBody>
        <Formik
          enableReinitialize
          initialValues={editing}
          validationSchema={unitsSchema}
          onSubmit={onSubmit}
        >
          {({ values, setValues }) => (
            <TemplateForm>
              <div className="d-flex">
                <ValidatedInput type="text" name="unit" label="Unit" />
                <FormGroup className="mb-0">
                  <Label>Unit conversion</Label>
                  <ValidatedInput
                    type="number"
                    name="lbsPerUnit"
                    min={0}
                    step="any"
                    className="mr-2"
                    unitSpec={unitSpec}
                    unit="" // Hide units because they are displayed in the dropdown
                  >
                    {({ renderInput, renderError }) => (
                      <FormGroup>
                        <div className="d-flex">
                          {customUnitType.value === 'lbsPerUnit' &&
                            renderInput()}
                          <DropdownButton
                            inModal
                            outline
                            size="sm"
                            options={unitTypeOptions}
                            selectedOption={selectedUnitName}
                            setOption={(option) => {
                              const { value: newUnitType } = option;
                              const { value: oldUnitType } = customUnitType;
                              if (oldUnitType === newUnitType) return;
                              if (newUnitType === 'unitsPerTree') {
                                setValues({
                                  ...values,
                                  unitsPerTree: [],
                                  lbsPerUnit: null,
                                });
                              } else {
                                setValues({
                                  ...values,
                                  lbsPerUnit: '',
                                  unitsPerTree: null,
                                });
                              }
                              setCustomUnitType(option);
                            }}
                          />
                        </div>
                        {customUnitType.value === 'lbsPerUnit' && renderError()}
                      </FormGroup>
                    )}
                  </ValidatedInput>
                </FormGroup>
              </div>
              <Button onClick={onClose} color="secondary" className="mr-2">
                Cancel
              </Button>
              <SubmitButton>Done</SubmitButton>
            </TemplateForm>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default RevenueUnitsModal;
